import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function AdminPage({ onBack }) {
  const { logout } = useAuth();
  const [loginHistory, setLoginHistory] = useState([]);
  const [sortBy, setSortBy] = useState('lastLogin'); // 'lastLogin' or 'count'

  useEffect(() => {
    loadLoginHistory();
  }, []);

  const loadLoginHistory = () => {
    try {
      const history = JSON.parse(localStorage.getItem('loginHistory') || '{}');
      const users = Object.values(history).map(user => ({
        ...user,
        lastLoginTime: user.lastLoginTime ? new Date(user.lastLoginTime) : null
      }));
      setLoginHistory(users);
    } catch (error) {
      console.error('Error loading login history:', error);
    }
  };

  const sortedUsers = [...loginHistory].sort((a, b) => {
    if (sortBy === 'lastLogin') {
      if (!a.lastLoginTime) return 1;
      if (!b.lastLoginTime) return -1;
      return b.lastLoginTime - a.lastLoginTime;
    } else if (sortBy === 'count') {
      return b.loginCount - a.loginCount;
    }
    return 0;
  });

  const formatTime = (date) => {
    if (!date) return 'Never';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatTime(date);
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all login history? This action cannot be undone.')) {
      localStorage.removeItem('loginHistory');
      setLoginHistory([]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Admin Panel</h1>
            <p className="text-gray-600">User Login Analytics & Statistics</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onBack}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={logout}
              className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-semibold text-gray-600 mb-2">Total Users</div>
            <div className="text-4xl font-bold text-blue-600">{sortedUsers.length}</div>
            <div className="text-xs text-gray-500 mt-2">Unique users who have logged in</div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-semibold text-gray-600 mb-2">Total Logins</div>
            <div className="text-4xl font-bold text-green-600">
              {sortedUsers.reduce((sum, user) => sum + user.loginCount, 0)}
            </div>
            <div className="text-xs text-gray-500 mt-2">Combined login attempts</div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-semibold text-gray-600 mb-2">Avg Logins per User</div>
            <div className="text-4xl font-bold text-purple-600">
              {sortedUsers.length > 0
                ? (
                    sortedUsers.reduce((sum, user) => sum + user.loginCount, 0) /
                    sortedUsers.length
                  ).toFixed(1)
                : '0'}
            </div>
            <div className="text-xs text-gray-500 mt-2">Average logins per user</div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Table Header with Controls */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">User Login History</h2>
              <div className="flex gap-3">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="lastLogin">Sort by: Last Login</option>
                  <option value="count">Sort by: Login Count</option>
                </select>
                <button
                  onClick={handleClearHistory}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Clear History
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          {sortedUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Total Logins
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Time Ago
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                          {user.loginCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatTime(user.lastLoginTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded">
                          {getTimeAgo(user.lastLoginTime)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-500">No login history recorded yet.</p>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Note:</span> Login history is stored locally in your browser. Data persists across sessions but is not synced to a server.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
